import { useEffect, useRef } from 'react';

export type SwipeDirection = 'left' | 'right';

// Movement before a touch is classified as a swipe or a scroll.
const SLOP_PX = 8;
// Horizontal travel that commits a slow swipe.
const COMMIT_DISTANCE_PX = 56;
// A quick flick commits sooner: this much travel at this speed (px/ms).
const FLING_DISTANCE_PX = 28;
const FLING_VELOCITY_PX_PER_MS = 0.35;
// The gesture must be clearly horizontal — |dx| beats |dy| by this ratio — both
// when it is classified and when it ends, so a diagonal scroll never switches.
const HORIZONTAL_RATIO = 1.4;
// Mobile browsers own back/forward swipes that start at the screen edges.
const EDGE_ZONE_PX = 20;
// How long a committed swipe stays armed to swallow the browser's synthesized
// click. Long enough to cover the compatibility event, short enough that a
// deliberate tap right after a swipe still lands.
const CLICK_SWALLOW_MS = 400;
// A hold before moving is a text selection or a drag, not a swipe.
const LONG_PRESS_MS = 300;

// Surfaces that must keep a horizontal drag to themselves.
const IGNORE_SELECTOR =
  '.MuiPopover-root, .MuiDrawer-root, .MuiDialog-root, .MuiModal-root, input[type="range"]';

/** True when something between ``start`` and the body scrolls horizontally on
 * its own (a wide table in a chat answer, an overflowing chip row). */
function hasHorizontalScrollAncestor(start: Element | null): boolean {
  let el = start;
  while (el && el !== document.body) {
    if (el instanceof HTMLElement) {
      const { overflowX } = getComputedStyle(el);
      if ((overflowX === 'auto' || overflowX === 'scroll') && el.scrollWidth > el.clientWidth + 1) {
        return true;
      }
    }
    el = el.parentElement;
  }
  return false;
}

/**
 * Document-wide horizontal swipe for the mobile bottom tabs: a finger swipe
 * anywhere on the screen — not only on the bar — calls ``onSwipe`` with the
 * direction the finger moved. Listeners are passive, so vertical scrolling
 * is untouched; a gesture only counts once it is clearly horizontal, past the
 * commit distance or flicked, and did not start on a horizontally scrollable
 * element, an open menu / dialog, or the browser's own edge-swipe zone.
 */
export function useMobileTabSwipe(enabled: boolean, onSwipe: (direction: SwipeDirection) => void) {
  // Read through a ref so the listeners attach once per ``enabled`` change and
  // a tab switch mid-gesture never tears down the gesture in flight.
  const onSwipeRef = useRef(onSwipe);
  onSwipeRef.current = onSwipe;

  useEffect(() => {
    if (!enabled || typeof document === 'undefined') return undefined;

    type Mode = 'idle' | 'pending' | 'horizontal';
    let mode: Mode = 'idle';
    let startX = 0;
    let startY = 0;
    let startTime = 0;
    let lastX = 0;
    let lastY = 0;
    let lastTime = 0;
    let velocity = 0; // px/ms, positive = finger moving right

    const reset = () => {
      mode = 'idle';
    };

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return reset();
      const touch = e.touches[0];
      if (touch.clientX < EDGE_ZONE_PX || touch.clientX > window.innerWidth - EDGE_ZONE_PX) {
        return reset();
      }
      const target = e.target instanceof Element ? e.target : null;
      if (target?.closest(IGNORE_SELECTOR) || hasHorizontalScrollAncestor(target)) return reset();
      mode = 'pending';
      startX = touch.clientX;
      startY = touch.clientY;
      startTime = performance.now();
      lastX = startX;
      lastY = startY;
      lastTime = startTime;
      velocity = 0;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (mode === 'idle') return;
      if (e.touches.length !== 1) return reset();
      const touch = e.touches[0];
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      const now = performance.now();
      if (mode === 'pending') {
        if (Math.abs(dx) < SLOP_PX && Math.abs(dy) < SLOP_PX) return;
        if (now - startTime > LONG_PRESS_MS || Math.abs(dx) <= Math.abs(dy) * HORIZONTAL_RATIO) {
          return reset();
        }
        mode = 'horizontal';
      }
      const dt = now - lastTime;
      if (dt > 0) velocity = (touch.clientX - lastX) / dt;
      lastX = touch.clientX;
      lastY = touch.clientY;
      lastTime = now;
    };

    // A committed swipe often starts on a candidate row, chip, or tab. Left
    // alone, the browser follows the gesture's ``touchend`` with a synthesized
    // click on whatever was under the finger — so one swipe would both change
    // the tab and activate a control, on the screen being left or the one
    // arriving in its place. ``preventDefault`` on this (non-passive)
    // ``touchend`` suppresses that compatibility click; the capture-phase
    // swallow below covers browsers that dispatch it anyway, and expires on
    // its own so a real tap right after a swipe still works.
    const swallowNextClick = () => {
      const swallow = (e: MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
      };
      document.addEventListener('click', swallow, { capture: true, once: true });
      window.setTimeout(() => {
        document.removeEventListener('click', swallow, { capture: true });
      }, CLICK_SWALLOW_MS);
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (mode !== 'horizontal') return reset();
      const dx = lastX - startX;
      const dy = lastY - startY;
      reset();
      if (Math.abs(dx) <= Math.abs(dy) * HORIZONTAL_RATIO) return;
      const flicked =
        Math.abs(dx) >= FLING_DISTANCE_PX &&
        Math.abs(velocity) >= FLING_VELOCITY_PX_PER_MS &&
        Math.sign(velocity) === Math.sign(dx);
      if (Math.abs(dx) >= COMMIT_DISTANCE_PX || flicked) {
        if (e.cancelable) e.preventDefault();
        swallowNextClick();
        onSwipeRef.current(dx < 0 ? 'left' : 'right');
      }
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    document.addEventListener('touchend', onTouchEnd);
    document.addEventListener('touchcancel', reset);
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', reset);
    };
  }, [enabled]);
}

export default useMobileTabSwipe;
